import React, { FC, useEffect, useState } from "react";
import * as Highcharts from "highcharts";

import LinearChart from "../../../../../../components/modules/dashboards/сharts/defaultCharts/linearChart/LinearChart";
import { BaseChartOptions } from "../../../../phases/phaseschart";
import {applyTipWith, checkWindowSize, wideFontSize24} from "../../../../../../utils/utils";
import { HCColumnRangeLine } from "../../../../../../utils/highchart/HighchartColumnRangeLine";
import { HCDateLabelsStyle } from "../../../../../../utils/highchart/labels";
import { positionerWithOffset } from "../../../../../../utils/highchart/tooltip";
import { useTypedSelector } from "@hooks/useTypedSelector";
import { formatNumber2d } from "@utils/format/number";
import { CropInfo } from "../../../../../../store/reducers/dashboard/crops/types";
import InlineTooltip from "@modules/dashboards/dashboardTooltips/TooltipVariants/InlineTooltip/InlineTooltip";
import Scrollbar from "@utils/highchart/Scrollbar";
import { getYear } from "date-fns";

interface ICropRotationChart extends BaseChartOptions {
    listOfCropsForDash: CropInfo[];
    chart?: Highcharts.Chart;
}

const CropRotationChart: FC<ICropRotationChart> = (props) => {
    let { listOfCropsForDash = [], chart } = props;

    const [chartOptions, setChartOptions] = useState<Highcharts.Options>({ title: { text: '' } });

    const { selectedDate } = useTypedSelector(state => state.selectedDate)

    useEffect(() => {
        const year = getYear(selectedDate || new Date())
        const minChartValue = new Date(Date.UTC(year, 0)).getTime();
        const maxChartValue = new Date(Date.UTC(year, 11, 31)).getTime();

        const defaultOptions: Highcharts.Options = {
            chart: {
                type: "columnrange",
                inverted: true,
                height: 375,
                marginRight: 20,
                spacingBottom: 0,
                zooming: {
                    type: 'y',
                    // @ts-ignore
                    mouseWheel: {
                        enabled: false
                    },
                    resetButton: {
                        theme: {
                            zIndex: 99
                        }
                    }
                }
            },
            credits: {
              enabled: false
            },
            title: {
                text: "",
            },
            xAxis: {
                labels: {
                    enabled: false
                },
                visible: true,
                categories: listOfCropsForDash.filter(crop => crop.cropId !== 'unknown').map(crop => crop.crops) || [],
                max: 6,
                scrollbar: Scrollbar(),
                lineColor: 'rgba(205, 206, 209, 1)',
                tickColor: 'rgba(205, 206, 209, 1)'
            },
            yAxis: {
                min: minChartValue,
                max: maxChartValue,
                tickmarkPlacement: 'between',
                showEmpty: true,
                type: 'datetime',
                opposite: true,
                dateTimeLabelFormats: {
                    month: '%b',
                },
                labels: {
                    autoRotation: undefined,
                    style: HCDateLabelsStyle({
                        fontSize: wideFontSize24(),
                        textAlign: 'center'
                    }),
                    x: 35,
                    overflow: 'allow',
                },
                minTickInterval: 3600 * 24 * 1000,//time in milliseconds
                minRange: 3600 * 24 * 1000,
                title: {
                    text: "",
                },
                endOnTick: false,
                scrollbar: Scrollbar({ height: 8, margin: 20 }),
            },
            tooltip: applyTipWith((ctx) => {
                // @ts-ignore
                return <InlineTooltip value={`${formatNumber2d(ctx.point.series.options.ha)} Га`}/>
            }, {
                shared: false,
                outside: true,
                positioner: positionerWithOffset({ x: 30, y: -20 })
            }),
            plotOptions: {
                series: {
                    states: {
                        hover: { enabled: false },
                        inactive: {
                            opacity: 1
                        }
                    },
                    stickyTracking: false,
                },
                columnrange: {
                    tooltip: {
                        followPointer: true,
                        followTouchMove: true
                    },
                    pointWidth: 20,
                    borderRadius: 6,
                    dataLabels: {
                        enabled: false,
                        formatter() {
                            return `${this.y}`;
                        },
                    }
                },
            },
            legend: {
                enabled: false
            },

            series: listOfCropsForDash.filter((crop: CropInfo)  => crop.cropId !== 'unknown').map((crops: CropInfo, i) => {
                return {
                    type: "columnrange",
                    ha: crops.area.value,
                    id: crops.cropId,
                    name: crops.crops,
                    color: crops.color,
                    data: crops.datePeriod.filter(([st, en]) => st !== 0 && en !== 0).map(([st, en]) => ({
                        x: i,
                        low: st,
                        high: en > maxChartValue ? maxChartValue : en
                    })),
                    pointWidth: checkWindowSize(36, 20),
                };
            })
        };

        setChartOptions(defaultOptions as Highcharts.Options);
    }, [listOfCropsForDash, selectedDate]);

    return <HCColumnRangeLine chart={chart}>
        <LinearChart
            callback={props.apiCallback}
            options={chartOptions}
            isStock
            isHcMore
            zoomAxis={'yAxis'}
            preventScrollWhenZoom={false}
        />
    </HCColumnRangeLine>;
};
export default CropRotationChart;
